/**
 * Animates document.title with a rotating pipe while transport is playing.
 * Syncs with StateManager `transportPlaying` (MIDI Start/Continue/Stop and internal clock).
 * Advances one frame per musical quarter note using `globalBPM`.
 */

const SPINNER_FRAMES = ['|', '/', '—', '\\'];

/** BPM limits avoid divide-by-zero and absurdly fast tab updates. */
const BPM_MIN = 20;
const BPM_MAX = 400;

function prefersReducedMotion() {
  return (
    typeof matchMedia !== 'undefined' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Milliseconds per quarter-note beat.
 * @param {import('../core/StateManager.js').StateManager} state
 */
function beatIntervalMs(state) {
  const raw = Number(state.get('globalBPM'));
  const bpm = Number.isFinite(raw) ? raw : 120;
  const clamped = Math.min(BPM_MAX, Math.max(BPM_MIN, bpm));
  return 60000 / clamped;
}

/**
 * @param {import('../core/StateManager.js').StateManager} state
 * @param {{ baseTitle?: string }} [options]
 * @returns {() => void} disposer
 */
export function bindTitleTransportSpinner(state, options = {}) {
  const baseTitle = options.baseTitle ?? document.title;
  let frameIndex = 0;
  /** @type {ReturnType<typeof setInterval> | null} */
  let timer = null;

  function renderSpinning() {
    document.title = `${baseTitle} ${SPINNER_FRAMES[frameIndex]}`;
  }

  function clearBeatTimer() {
    if (timer != null) {
      clearInterval(timer);
      timer = null;
    }
  }

  function scheduleBeatTimer() {
    clearBeatTimer();
    timer = window.setInterval(() => {
      frameIndex = (frameIndex + 1) % SPINNER_FRAMES.length;
      renderSpinning();
    }, beatIntervalMs(state));
  }

  function startSpinner() {
    if (prefersReducedMotion()) {
      document.title = baseTitle;
      return;
    }
    frameIndex = 0;
    renderSpinning();
    scheduleBeatTimer();
  }

  function stopSpinner() {
    clearBeatTimer();
    frameIndex = 0;
    document.title = baseTitle;
  }

  /** Reschedule interval when BPM changes; keep current frame. */
  function onBpmChange() {
    if (state.get('transportPlaying') !== true) return;
    if (prefersReducedMotion()) return;
    renderSpinning();
    scheduleBeatTimer();
  }

  function sync(playing) {
    if (playing) startSpinner();
    else stopSpinner();
  }

  const unsubTransport = state.subscribe('transportPlaying', sync);
  const unsubBpm = state.subscribe('globalBPM', onBpmChange);
  sync(state.get('transportPlaying') === true);

  return () => {
    unsubTransport();
    unsubBpm();
    stopSpinner();
  };
}
